import { useState, useEffect, useCallback } from 'react';
import { Listing } from '@/types/listing';

interface Props {
  listing: Listing;
  onClose: () => void;
}

export default function ListingGalleryModal({ listing, onClose }: Props) {
  const [activeIndex, setActiveIndex] = useState(0);
  const images = listing.images ?? [];

  const prev = useCallback(() => {
    setActiveIndex((i) => (i === 0 ? images.length - 1 : i - 1));
  }, [images.length]);

  const next = useCallback(() => {
    setActiveIndex((i) => (i === images.length - 1 ? 0 : i + 1));
  }, [images.length]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') prev();
      if (e.key === 'ArrowRight') next();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose, prev, next]);

  // Prevent body scroll
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  if (images.length === 0) return null;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/95 flex flex-col"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      {/* Top bar */}
      <div className="flex items-center justify-between px-6 py-4 flex-shrink-0">
        <div className="min-w-0">
          <p className="text-white font-semibold text-base truncate">{listing.address}</p>
          <p className="text-white/50 text-sm">{listing.price} &middot; {listing.beds} bd &middot; {listing.baths} ba &middot; {listing.sqft} sq ft</p>
        </div>
        <div className="flex items-center gap-4 ml-4 flex-shrink-0">
          <span className="text-white/40 text-sm">{activeIndex + 1} / {images.length}</span>
          <button
            onClick={onClose}
            className="w-9 h-9 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-white text-lg"></i>
          </button>
        </div>
      </div>

      {/* Main image area */}
      <div className="flex-1 flex items-center justify-center relative min-h-0 px-16">
        <img
          key={activeIndex}
          src={images[activeIndex]}
          alt={`${listing.address} photo ${activeIndex + 1}`}
          className="max-h-full max-w-full object-contain rounded-lg select-none animate-fade-in"
        />

        {images.length > 1 && (
          <>
            <button
              onClick={prev}
              className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/25 transition-colors cursor-pointer"
            >
              <i className="ri-arrow-left-s-line text-white text-xl"></i>
            </button>
            <button
              onClick={next}
              className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/25 transition-colors cursor-pointer"
            >
              <i className="ri-arrow-right-s-line text-white text-xl"></i>
            </button>
          </>
        )}
      </div>

      {/* Thumbnail strip */}
      {images.length > 1 && (
        <div className="flex-shrink-0 px-6 py-4">
          <div className="flex gap-2 overflow-x-auto justify-center pb-1">
            {images.map((src, idx) => (
              <button
                key={idx}
                onClick={() => setActiveIndex(idx)}
                className={`flex-shrink-0 w-16 h-12 rounded overflow-hidden transition-all cursor-pointer ${
                  idx === activeIndex
                    ? 'ring-2 ring-amber-400 opacity-100'
                    : 'opacity-40 hover:opacity-70'
                }`}
              >
                <img
                  src={src}
                  alt={`thumb ${idx + 1}`}
                  className="w-full h-full object-cover object-top"
                />
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
